import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Tag, Input, Select, Space, Card, Typography, Modal, Form, message,
  Switch, InputNumber, Popconfirm, Tooltip,
} from 'antd';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';
import { propertiesApi } from '../../services/api';

const { Title } = Typography;

const typeColors: Record<string, string> = {
  VILLA: 'green', APARTMENT: 'blue', FINCA: 'orange',
  PENTHOUSE: 'purple', TOWNHOUSE: 'cyan', COMMERCIAL: 'red', LAND: 'gold',
  GARAGE: 'geekblue', OFFICE: 'magenta', STORAGE: 'volcano',
  BUILDING: 'lime', ROOM: 'processing', OTHER: 'default',
};

const statusColors: Record<string, string> = {
  AVAILABLE: 'green', RESERVED: 'orange', SOLD: 'red', RENTED: 'blue', OFF_MARKET: 'default',
};

export default function PropertiesPage() {
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<any>({});
  const [search, setSearch] = useState('');
  const [type, setType] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchProperties = () => {
    setLoading(true);
    propertiesApi.list({ page, limit: 20, search: search || undefined, type })
      .then((r) => { setProperties(r.data.data); setMeta(r.data.meta); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProperties(); }, [page, type]);

  const openCreate = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const res = await propertiesApi.create(values);
      message.success('Propiedad creada');
      setModalOpen(false);
      form.resetFields();
      // Navigate to the new property edit page
      navigate(`/properties/${res.data.id}`);
    } catch {
      message.error('Error al crear');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await propertiesApi.delete(id);
      message.success('Propiedad eliminada');
      fetchProperties();
    } catch {
      message.error('Error al eliminar');
    }
  };

  const columns = [
    { title: 'Título', dataIndex: 'title', key: 'title', ellipsis: true },
    { title: 'Ref.', dataIndex: 'reference', key: 'reference' },
    {
      title: 'Tipo', dataIndex: 'type', key: 'type',
      render: (t: string) => <Tag color={typeColors[t]}>{t}</Tag>,
    },
    {
      title: 'Estado', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={statusColors[s]}>{s}</Tag>,
    },
    {
      title: 'Precio', dataIndex: 'price', key: 'price',
      render: (p: number) => `€${p?.toLocaleString() || 0}`,
    },
    { title: 'Ciudad', dataIndex: 'city', key: 'city' },
    { title: 'Hab.', dataIndex: 'bedrooms', key: 'bedrooms' },
    { title: 'm²', dataIndex: 'size', key: 'size' },
    {
      title: 'Pub.', dataIndex: 'published', key: 'published',
      render: (p: boolean) => p ? <Tag color="green">Sí</Tag> : <Tag>No</Tag>,
    },
    {
      title: 'Acciones', key: 'actions', width: 120,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Tooltip title="Ver / Editar"><Button type="link" size="small" icon={<Pencil size={14} />} onClick={() => navigate(`/properties/${r.id}`)} /></Tooltip>
          <Popconfirm title="¿Eliminar esta propiedad?" onConfirm={() => handleDelete(r.id)} okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}>
            <Tooltip title="Eliminar"><Button type="link" size="small" danger icon={<Trash2 size={14} />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Propiedades</Title>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>Nueva Propiedad</Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="Buscar..." prefix={<Search size={14} />} value={search} onChange={(e) => setSearch(e.target.value)} onPressEnter={fetchProperties} style={{ width: 250 }} />
          <Select placeholder="Tipo" allowClear style={{ width: 160 }} value={type} onChange={setType} options={Object.keys(typeColors).map((k) => ({ value: k, label: k }))} />
        </Space>
      </Card>

      <Card>
        <Table columns={columns} dataSource={properties} loading={loading} rowKey="id" size="small" scroll={{ x: 1000 }}
          onRow={(r) => ({ onClick: () => navigate(`/properties/${r.id}`), style: { cursor: 'pointer' } })}
          pagination={{ current: page, pageSize: 20, total: meta.total, onChange: setPage, showTotal: (t) => `${t} propiedades` }}
        />
      </Card>

      <Modal title="Nueva Propiedad" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="Crear" width={720} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="type" label="Tipo" initialValue="VILLA">
              <Select options={Object.keys(typeColors).map((k) => ({ value: k, label: k }))} />
            </Form.Item>
            <Form.Item name="price" label="Precio (€)" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} step={1000} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={(v) => (Number(v?.replace(/\./g, '')) || 0) as any} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="city" label="Ciudad"><Input /></Form.Item>
            <Form.Item name="zone" label="Zona"><Input /></Form.Item>
            <Form.Item name="address" label="Dirección"><Input /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="bedrooms" label="Habitaciones"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
            <Form.Item name="bathrooms" label="Baños"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
            <Form.Item name="size" label="Superficie (m²)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
            <Form.Item name="plotSize" label="Parcela (m²)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="yearBuilt" label="Año construcción"><InputNumber style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="energyRating" label="Certificado energético">
              <Select allowClear options={['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(v => ({ value: v, label: v }))} />
            </Form.Item>
            <Form.Item name="reference" label="Referencia"><Input placeholder="SWM-004" /></Form.Item>
          </div>
          <Form.Item name="description" label="Descripción"><Input.TextArea rows={3} /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
            <Form.Item name="pool" label="Piscina" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="garden" label="Jardín" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="terrace" label="Terraza" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="seaView" label="Vista mar" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="garage" label="Garaje" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="airConditioning" label="A/C" valuePropName="checked"><Switch /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="featured" label="Destacada" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="published" label="Publicar en web" valuePropName="checked" initialValue={true}><Switch /></Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
